import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

/**
 * A generic single-choice replacement for a plain native <select> -- same
 * reasoning as CountrySelect.jsx/DatePicker.jsx/TimeSlotPicker.jsx: a
 * native <select>'s own popup list is OS/browser chrome that can't be
 * restyled. `options` is a flat array of strings, each used as both value
 * and label. Same value/onChange contract as a native <select> would have
 * (onChange receives the picked string directly), so callers swap it in
 * without touching any state/validation/payload logic. The panel portals
 * to document.body and takes its width/position from the trigger's own
 * bounding rect, so it lines up exactly with the field instead of
 * floating at a fixed size.
 */
export default function Dropdown({ id, testId, value, onChange, options, placeholder = "Select an option", hasError, disabled }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const updateRect = () => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (opt) => {
    onChange(opt);
    setOpen(false);
  };

  const panelStyle = useMemo(() => {
    if (!rect) return { display: "none" };
    return { position: "fixed", top: rect.bottom + 6, left: rect.left, width: rect.width };
  }, [rect]);

  const fieldBase =
    "w-full flex items-center gap-2 rounded-md px-3.5 py-2.5 text-sm bg-white border text-left focus:outline-none focus:ring-2 focus:ring-royal/30 focus:border-royal";

  return (
    <div>
      <button
        type="button"
        id={id}
        data-testid={testId}
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={Boolean(hasError)}
        className={`${fieldBase} ${hasError ? "border-red-400" : "border-line"} ${disabled ? "opacity-70 cursor-not-allowed" : ""} ${value ? "text-ink" : "text-ink-soft/70"}`}
      >
        <span className="flex-1 truncate">{value || placeholder}</span>
        <ChevronDown aria-hidden size={14} className="text-ink-soft flex-shrink-0" />
      </button>

      {open &&
        createPortal(
          <div ref={panelRef} style={panelStyle} className="zt-dropdown-popover" role="listbox" aria-label={placeholder}>
            {options.map((opt) => (
              <button
                type="button"
                key={opt}
                role="option"
                aria-selected={opt === value}
                onClick={() => pick(opt)}
                className={`zt-dropdown-option ${opt === value ? "is-selected" : ""}`}
              >
                <span>{opt}</span>
                {opt === value && <Check aria-hidden size={14} className="zt-dropdown-check" />}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
